// Filtros da biblioteca de mídias: estado da tela ↔ query string da URL (link compartilhável)
// e montagem da query de GET /media. Funções puras, testadas em mediaFilters.test.ts.
import { toQuery } from '@services/apiResponse';
import type {
  MediaFilter,
  MediaKind,
  MediaOrientation,
  MediaSortBy,
  MediaSource,
  MediaStatus,
} from '@/types/media';

/** "Liberada para cliente": todas | sim | não. */
export type ReleasedFilter = '' | 'true' | 'false';

export interface MediaListParams {
  search: string;
  kind: MediaKind | '';
  tagIds: number[];
  eventId: string;
  dateFrom: string;
  dateTo: string;
  preferred: boolean;
  released: ReleasedFilter;
  status: MediaStatus | '';
  orientation: MediaOrientation | '';
  source: MediaSource | '';
  sortBy: MediaSortBy | '';
  sortDirection: 'asc' | 'desc' | '';
  page: number;
}

const KINDS: MediaKind[] = ['image', 'video'];
const STATUSES: MediaStatus[] = ['pending', 'processing', 'ready', 'error'];
const ORIENTATIONS: MediaOrientation[] = ['landscape', 'portrait', 'square'];
const SOURCES: MediaSource[] = ['upload', 'onedrive', 'landing_page'];
export const MEDIA_SORTS: MediaSortBy[] = [
  'relevance',
  'content_date',
  'created_at',
  'size_bytes',
  'file_name',
  'duration_seconds',
];

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | '' =>
  value && (allowed as readonly string[]).includes(value) ? (value as T) : '';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "1,2, x,3" → [1, 2, 3] (só inteiros positivos, sem repetição). */
export const parseIdList = (value: string | null | undefined): number[] => {
  if (!value) return [];
  const out: number[] = [];
  for (const part of value.split(',')) {
    const n = Number(part.trim());
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out;
};

export const emptyMediaParams = (): MediaListParams => ({
  search: '',
  kind: '',
  tagIds: [],
  eventId: '',
  dateFrom: '',
  dateTo: '',
  preferred: false,
  released: '',
  status: '',
  orientation: '',
  source: '',
  sortBy: '',
  sortDirection: '',
  page: 1,
});

/** Lê os filtros da URL (`?q=&kind=&tags=1,2&event=&from=&to=&preferred=1&released=&status=…`). */
export const mediaParamsFromSearch = (p: URLSearchParams): MediaListParams => {
  const page = Number(p.get('page'));
  const eventId = p.get('event') ?? '';
  const released = p.get('released');
  const dir = p.get('dir');
  return {
    search: p.get('q') ?? '',
    kind: oneOf(p.get('kind'), KINDS),
    tagIds: parseIdList(p.get('tags')),
    eventId: /^\d+$/.test(eventId) ? eventId : '',
    dateFrom: ISO_DATE.test(p.get('from') ?? '') ? (p.get('from') as string) : '',
    dateTo: ISO_DATE.test(p.get('to') ?? '') ? (p.get('to') as string) : '',
    preferred: p.get('preferred') === '1',
    released: released === 'true' || released === 'false' ? released : '',
    status: oneOf(p.get('status'), STATUSES),
    orientation: oneOf(p.get('orientation'), ORIENTATIONS),
    source: oneOf(p.get('source'), SOURCES),
    sortBy: oneOf(p.get('sort'), MEDIA_SORTS),
    sortDirection: dir === 'asc' || dir === 'desc' ? dir : '',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
};

/** Chaves de filtro que esta tela controla na URL (as demais — ex.: `media` — são preservadas). */
const FILTER_KEYS = [
  'q', 'kind', 'tags', 'event', 'from', 'to', 'preferred', 'released',
  'status', 'orientation', 'source', 'sort', 'dir', 'page',
];

/** Grava os filtros na URL, omitindo os vazios e preservando os parâmetros alheios. */
export const mediaParamsToSearch = (params: MediaListParams, base?: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(base);
  FILTER_KEYS.forEach((k) => next.delete(k));
  const set = (key: string, value: string) => {
    if (value) next.set(key, value);
  };
  set('q', params.search.trim());
  set('kind', params.kind);
  set('tags', params.tagIds.join(','));
  set('event', params.eventId);
  set('from', params.dateFrom);
  set('to', params.dateTo);
  set('preferred', params.preferred ? '1' : '');
  set('released', params.released);
  set('status', params.status);
  set('orientation', params.orientation);
  set('source', params.source);
  set('sort', params.sortBy);
  set('dir', params.sortDirection);
  set('page', params.page > 1 ? String(params.page) : '');
  return next;
};

/** Há algum filtro (além de página/ordenação) aplicado? */
export const hasMediaFilters = (params: MediaListParams): boolean =>
  !!(
    params.search.trim() ||
    params.kind ||
    params.tagIds.length ||
    params.eventId ||
    params.dateFrom ||
    params.dateTo ||
    params.preferred ||
    params.released ||
    params.status ||
    params.orientation ||
    params.source
  );

/** Estado da tela → filtro da API. */
export const toMediaFilter = (params: MediaListParams, pageSize: number): MediaFilter => ({
  search: params.search.trim() || undefined,
  kind: params.kind || undefined,
  tag_ids: params.tagIds.length > 0 ? params.tagIds : undefined,
  event_id: params.eventId ? Number(params.eventId) : undefined,
  date_from: params.dateFrom || undefined,
  date_to: params.dateTo || undefined,
  preferred: params.preferred || undefined,
  released: params.released === '' ? undefined : params.released === 'true',
  status: params.status || undefined,
  orientation: params.orientation || undefined,
  source: params.source || undefined,
  sort_by: params.sortBy || undefined,
  sort_direction: params.sortBy ? params.sortDirection || undefined : undefined,
  page: params.page,
  page_size: pageSize,
});

/**
 * Query string de GET /media. `released=false` é um filtro de verdade (só as NÃO liberadas),
 * por isso vai como texto — o `toQuery` descarta o booleano `false`.
 */
export const buildMediaQuery = (filter: MediaFilter): string =>
  toQuery({
    search: filter.search?.trim() || undefined,
    kind: filter.kind || undefined,
    tag_ids: filter.tag_ids && filter.tag_ids.length > 0 ? filter.tag_ids.join(',') : undefined,
    event_id: filter.event_id,
    date_from: filter.date_from,
    date_to: filter.date_to,
    preferred: filter.preferred ? 'true' : undefined,
    released: filter.released === undefined ? undefined : String(filter.released),
    status: filter.status || undefined,
    orientation: filter.orientation || undefined,
    source: filter.source || undefined,
    product_sku: filter.product_sku?.trim() || undefined,
    sort_by: filter.sort_by,
    sort_direction: filter.sort_direction,
    page: filter.page,
    page_size: filter.page_size === undefined ? undefined : Math.min(Math.max(filter.page_size, 1), 100),
  });
